import {useEffect, useRef, useState} from "react";
import {
    Box,
    Heading,
    HStack,
    IconButton,
    Text,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
    Button,
    useDisclosure,
    Progress,
    VStack,
    Divider,
    Tooltip
} from "@chakra-ui/react";
import {CalendarIcon, TriangleUpIcon, WarningTwoIcon, InfoIcon, CopyIcon} from '@chakra-ui/icons';
import {useNavigate, useLocation} from 'react-router-dom';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {formatDataItaliano, formatDataToolbar} from "../../utilities/formatDate";
import {it} from "date-fns/locale";
import './toolbar.scss';
import {useDate} from "../../contexts/DateContext";

const Toolbar = ({last_day, setDate}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const {isPolling, setIsPolling} = useDate();
    const calendarButtonRef = useRef(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [showCalendar, setShowCalendar] = useState(false);
    const [calendarPosition, setCalendarPosition] = useState({top: 0, left: 0});

    // Dialog per conferma toggle polling
    const {isOpen, onOpen, onClose} = useDisclosure();
    const cancelRef = useRef();

    const handleDashboardClick = () => {
        navigate('/');
    };

    const handleLogsClick = () => {
        navigate('/logs');
    };

    // Aggiorna la data selezionata quando last_day cambia
    useEffect(() => {
        if (last_day) {
            setSelectedDate(new Date(last_day));
        }
    }, [last_day]);

    // Posiziona dinamicamente il calendario sotto l'icona
    const handleCalendarClick = () => {
        const rect = calendarButtonRef.current.getBoundingClientRect();
        setCalendarPosition({
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX
        });
        setShowCalendar(prev => !prev);
    };

    // Gestione toggle polling
    const handlePollingToggle = () => {
        onOpen();
    };

    const confirmPollingToggle = () => {
        setIsPolling(prev => !prev);
        onClose();
    };

    return (
        <section className="toolbar">
            <HStack spacing="44px" justify="space-between" w="100%">
                <HStack spacing="44px">
                    <Heading as="h1" size="lg" noOfLines={1}>
                        <img src="/images/logo-small.png" alt="Logo" style={{height: '85px'}}/>
                    </Heading>

                    {!last_day ? (
                        <Text fontSize="lg">Loading Data...</Text>
                    ) : (
                        <>
                            <VStack spacing="0" align="flex-start" className="date">
                                <Text fontSize="xl" fontWeight="bold">
                                    {formatDataToolbar(last_day).weekday}
                                </Text>
                                <Text fontSize="sm">
                                    {formatDataToolbar(last_day).date}
                                </Text>
                            </VStack>

                            <HStack align="flex-start">
                                <Tooltip label="Seleziona data" placement="bottom" hasArrow>
                                    <IconButton
                                        ref={calendarButtonRef}
                                        icon={<CalendarIcon/>}
                                        aria-label="Seleziona data"
                                        onClick={handleCalendarClick}
                                        variant="outline"
                                        bg="#38A169"
                                        color="white"
                                        borderColor="#38A169"
                                        _hover={{
                                            bg: "#2F855A"
                                        }}
                                    />
                                </Tooltip>

                                <VStack spacing="4px" align="flex-start">
                                    <Tooltip
                                        label={isPolling ? "Ferma aggiornamento automatico" : "Avvia aggiornamento automatico"}
                                        placement="bottom"
                                        hasArrow
                                    >
                                        <IconButton
                                            icon={isPolling ? <TriangleUpIcon/> : <WarningTwoIcon/>}
                                            aria-label={isPolling ? "Ferma aggiornamento automatico" : "Avvia aggiornamento automatico"}
                                            onClick={handlePollingToggle}
                                            colorScheme={isPolling ? "green" : "orange"}
                                            variant="solid"
                                            transform={isPolling ? "rotate(90deg)" : "none"}
                                        />
                                    </Tooltip>
                                    {isPolling && (
                                        <Progress
                                            size="xs"
                                            isIndeterminate
                                            colorScheme="green"
                                            width="40px"
                                            borderRadius="md"
                                        />
                                    )}
                                </VStack>

                                <Divider orientation="vertical" height="40px" borderColor="gray.400" mx={2} />

                                <Tooltip label="Vai alla Dashboard" placement="bottom" hasArrow>
                                    <IconButton
                                        icon={<CopyIcon/>}
                                        aria-label="Vai alla Dashboard"
                                        onClick={handleDashboardClick}
                                        variant="outline"
                                        bg={location.pathname === '/' ? "#3182CE" : "#38A169"}
                                        color="white"
                                        borderColor={location.pathname === '/' ? "#3182CE" : "#38A169"}
                                        _hover={{
                                            bg: location.pathname === '/' ? "#2C5282" : "#2F855A"
                                        }}
                                    />
                                </Tooltip>

                                <Tooltip label="Vai ai Logs" placement="bottom" hasArrow>
                                    <IconButton
                                        icon={<InfoIcon/>}
                                        aria-label="Vai ai Logs"
                                        onClick={handleLogsClick}
                                        variant="outline"
                                        bg={location.pathname === '/logs' ? "#3182CE" : "#38A169"}
                                        color="white"
                                        borderColor={location.pathname === '/logs' ? "#3182CE" : "#38A169"}
                                        _hover={{
                                            bg: location.pathname === '/logs' ? "#2C5282" : "#2F855A"
                                        }}
                                    />
                                </Tooltip>
                            </HStack>
                        </>
                    )}
                </HStack>

                <Box>
                    <picture>
                        <source
                            media="(max-width: 768px)"
                            srcSet="/images/logo-druido-mobile.png"
                        />
                        <source
                            media="(min-width: 769px)"
                            srcSet="/images/logo-druido-desktop.png"
                        />
                        <img
                            src="/images/logo-druido-desktop.png"
                            alt="Druido Logo"
                            style={{height: '85px'}}
                            loading="lazy"
                        />
                    </picture>
                </Box>
            </HStack>

            {/* Calendario visibile solo se triggerato */}
            {showCalendar && selectedDate && (
                <Box
                    position="absolute"
                    top={calendarPosition.top + "px"}
                    left={calendarPosition.left + "px"}
                    zIndex="10"
                    bg="white"
                    boxShadow="md"
                    borderRadius="md"
                >
                    <DatePicker
                        selected={selectedDate}
                        onChange={(date) => {
                            if (!date) return;
                            const formatted = date.toISOString().split("T")[0];
                            setSelectedDate(date);
                            setShowCalendar(false);
                            setDate(formatted);
                        }}
                        inline
                        locale={it}
                        dateFormat="yyyy-MM-dd"
                    />

                </Box>
            )}

            {/* Dialog di conferma per toggle polling */}
            <AlertDialog
                isOpen={isOpen}
                leastDestructiveRef={cancelRef}
                onClose={onClose}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            {isPolling ? "Fermare il Polling?" : "Riavviare il Polling?"}
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            {isPolling
                                ? "Sei sicuro di voler fermare il polling dei dati? Gli aggiornamenti automatici saranno sospesi."
                                : "Sei sicuro di voler riavviare il polling dei dati? Gli aggiornamenti automatici riprenderanno ogni 3 secondi."}
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onClose}>
                                Annulla
                            </Button>
                            <Button
                                colorScheme={isPolling ? "green" : "orange"}
                                onClick={confirmPollingToggle}
                                ml={3}
                            >
                                {isPolling ? "Ferma" : "Riavvia"}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </section>
    );
};

export default Toolbar;
